// src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import { login } from '../services/api/auth';
import { useAuth } from '../context/AuthContext';
import { Input, Button, FormField, GoogleButton, OAuthDivider } from '../components/ui';
import { useToast } from '../components/ui/Toast';
import logo from '../images/logo_transparent.png';
import logoWhite from '../images/logo_transparent_white.png';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const { handleLogin: contextHandleLogin } = useAuth();

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const userData = await login(email, password);
            await contextHandleLogin(userData);

            toast.success('Welcome back!');
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Please check your credentials and try again');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen">
            {/* Left: Form Section */}
            <div className="flex w-full flex-col justify-center px-4 py-10 sm:px-6 lg:w-1/2 lg:px-20 xl:px-24">
                <div className="mx-auto w-full max-w-md">
                    {/* Logo */}
                    <div className="mb-10">
                        <Link to="/" className="inline-block">
                            <img src={logo} alt="Refinal" className="h-10" />
                        </Link>
                    </div>

                    {/* Header */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-black dark:text-white sm:text-title-md">
                            Sign In to Your Account
                        </h2>
                        <p className="mt-2 text-body">
                            Enter your email and password to access your dashboard
                        </p>
                    </div>

                    {/* Google Sign In */}
                    <GoogleButton text="Sign in with Google" />

                    {/* Divider */}
                    <OAuthDivider />

                    {/* Form */}
                    <form onSubmit={handleLogin} className="space-y-6">
                        <FormField
                            label="Email"
                            htmlFor="email"
                            required
                        >
                            <div className="relative">
                                <Input
                                    type="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-body">
                                    <FiMail size={20} />
                                </span>
                            </div>
                        </FormField>

                        <FormField
                            label="Password"
                            htmlFor="password"
                            required
                        >
                            <Input
                                type="password"
                                name="password"
                                placeholder="Enter your password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </FormField>

                        <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            loading={isLoading}
                            icon={FiLogIn}
                            className="w-full"
                        >
                            Sign In
                        </Button>
                    </form>

                    {/* Sign Up Link */}
                    <div className="mt-6 text-center">
                        <p className="text-body">
                            Don't have an account?{' '}
                            <Link
                                to="/register"
                                className="font-medium text-brand-500 hover:text-brand-600 transition-colors"
                            >
                                Sign Up
                            </Link>
                        </p>
                    </div>
                </div>
            </div>

            {/* Right: Branding Section */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-500 via-brand-600 to-brand-700 flex-col justify-center items-center p-12 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
                </div>

                {/* Content */}
                <div className="relative z-10 text-center">
                    <img src={logoWhite} alt="Refinal" className="h-12 mx-auto mb-8" />
                    <h2 className="text-4xl font-bold text-white mb-6">
                        Financial Analytics Platform
                    </h2>
                    <p className="text-xl text-white/80 max-w-md">
                        Aggregate transaction data from multiple payment processors and banks.
                        Get real-time insights into your financial performance.
                    </p>

                    {/* Features List */}
                    <div className="mt-12 space-y-4 text-left max-w-sm mx-auto">
                        <div className="flex items-center gap-3 text-white">
                            <div className="w-2 h-2 rounded-full bg-white" />
                            <span>Multi-tenant data isolation</span>
                        </div>
                        <div className="flex items-center gap-3 text-white">
                            <div className="w-2 h-2 rounded-full bg-white" />
                            <span>Stripe, Revolut & Coingate integrations</span>
                        </div>
                        <div className="flex items-center gap-3 text-white">
                            <div className="w-2 h-2 rounded-full bg-white" />
                            <span>AI-powered transaction tagging</span>
                        </div>
                        <div className="flex items-center gap-3 text-white">
                            <div className="w-2 h-2 rounded-full bg-white" />
                            <span>DCF valuation & forecasting</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;
